import { useEffect, useRef, useState } from "react";
import { auth, db, storage } from "../Firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { onAuthStateChanged, updateProfile } from "firebase/auth";
import SignatureCanvas from "react-signature-canvas";
import { toast } from "react-toastify";
import "../styles/Profile.css";

/* ================= LOCATION DATA ================= */
const locationData = {
  India: {
    "West Bengal": ["Kolkata", "Durgapur", "Siliguri"],
    Jharkhand: ["Ranchi", "Jamshedpur"],
    Bihar: ["Patna", "Gaya"]
  },
  USA: {
    California: ["Los Angeles", "San Diego"],
    Texas: ["Houston", "Dallas"]
  }
};

/* ================= DROPDOWN OPTIONS ================= */
const occupationOptions = [
  "Student",
  "Teacher",
  "Engineer",
  "Developer",
  "Designer",
  "Business",
  "Other"
];

const educationOptions = [
  "High School",
  "Diploma",
  "Bachelor",
  "Master",
  "PhD",
  "Other"
];

const Profile = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [mobile, setMobile] = useState("");
  const [biography, setBiography] = useState("");
  const [language, setLanguage] = useState("");

  const [website, setWebsite] = useState("");
  const [facebook, setFacebook] = useState("");
  const [instagram, setInstagram] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [x, setX] = useState("");
  const [youtube, setYoutube] = useState("");

  const [occupation, setOccupation] = useState("");
  const [education, setEducation] = useState("");
  const [college, setCollege] = useState("");

  const [country, setCountry] = useState("");
  const [stateName, setStateName] = useState("");
  const [city, setCity] = useState("");

  const [photo, setPhoto] = useState("");
  const [signature, setSignature] = useState("");

  const sigCanvasRef = useRef(null);

  /* ================= FETCH USER DATA ================= */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) return;

      const snap = await getDoc(doc(db, "users", user.uid));
      if (snap.exists()) {
        const data = snap.data();
        setUserData(data);

        setMobile(data.phone || "");
        setBiography(data.biography || "");
        setLanguage(data.language || "");

        setWebsite(data.website || "");
        setFacebook(data.facebook || "");
        setInstagram(data.instagram || "");
        setLinkedin(data.linkedin || "");
        setX(data.x || "");
        setYoutube(data.youtube || "");

        setOccupation(data.occupation || "");
        setEducation(data.education || "");
        setCollege(data.college || "");

        setCountry(data.country || "");
        setStateName(data.state || "");
        setCity(data.city || "");

        setPhoto(data.profileImage || user.photoURL || "");
        setSignature(data.signatureURL || "");
      }
    });

    return () => unsubscribe();
  }, []);

  /* ================= LOCATION HELPERS ================= */
  const countries = Object.keys(locationData);
  const states = country ? Object.keys(locationData[country] || {}) : [];
  const cities =
    country && stateName ? locationData[country][stateName] || [] : [];

  /* ================= PROFILE IMAGE ================= */
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !auth.currentUser) return;

    try {
      setLoading(true);
      const imageRef = ref(storage, `profile/${auth.currentUser.uid}.jpg`);
      await uploadBytes(imageRef, file);
      const url = await getDownloadURL(imageRef);

      await updateDoc(doc(db, "users", auth.currentUser.uid), {
        profileImage: url
      });

      await updateProfile(auth.currentUser, { photoURL: url });
      setPhoto(url);
      toast.success("Profile image updated");
    } finally {
      setLoading(false);
    }
  };

  /* ================= SAVE DIGITAL SIGNATURE ================= */
  const handleSaveSignature = async () => {
  if (!auth.currentUser || sigCanvasRef.current.isEmpty()) {
    toast.error("Please draw your signature");
    return;
  }

  try {
    setLoading(true);

    // ✅ USE getCanvas() INSTEAD OF getTrimmedCanvas()
    const canvas = sigCanvasRef.current.getCanvas();
    const dataUrl = canvas.toDataURL("image/png");

    const blob = await (await fetch(dataUrl)).blob();

    const signRef = ref(
      storage,
      `profile/signature/${auth.currentUser.uid}.png`
    );

    await uploadBytes(signRef, blob);
    const url = await getDownloadURL(signRef);

    await updateDoc(doc(db, "users", auth.currentUser.uid), {
      signatureURL: url
    });

    setSignature(url);
    sigCanvasRef.current.clear();
    toast.success("Signature saved");
  } catch (err) {
    console.error(err);
    toast.error("Failed to save signature");
  } finally {
    setLoading(false);
  }
};


  /* ================= SAVE PROFILE ================= */
  const handleSave = async () => {
    if (!auth.currentUser) return;

    try {
      setLoading(true);
      await updateDoc(doc(db, "users", auth.currentUser.uid), {
        phone: mobile,
        biography,
        language,
        website,
        facebook,
        instagram,
        linkedin,
        x,
        youtube,
        occupation,
        education,
        college,
        country,
        state: stateName,
        city
      });
      toast.success("Profile updated");
    } finally {
      setLoading(false);
    }
  };

  if (!userData) return <p className="loading">Loading...</p>;

  return (
    <div className="profile-container">
      <h2 className="profile-title">Public Profile</h2>

      <div className="profile-card">
        {/* LEFT */}
        <div className="profile-left">
          <div className="avatar-wrapper">
            <img
              src={photo || "/default-avatar.png"}
              alt="Profile"
              className="avatar"
            />
            <label className="upload-btn">
              📷
              <input type="file" hidden onChange={handleImageUpload} />
            </label>
          </div>
        </div>

        {/* RIGHT */}
        <div className="profile-right">
          <h3>Personal Information</h3>

          <div className="field">
            <label>Name</label>
            <input value={userData.name || ""} disabled />
          </div>

          <div className="field">
            <label>User Name</label>
            <input value={userData.username || ""} disabled />
          </div>
          {(!userData.role || userData.role === "student") && (
            <div className="field">
              <label>Certificate Name</label>
              <input value={userData.certificateName || ""} disabled />
            </div>
          )}


          <div className="field">
            <label>Email</label>
            <input value={userData.email || ""} disabled />
          </div>

          <div className="field">
            <label>Mobile</label>
            <input value={mobile} onChange={(e) => setMobile(e.target.value)} />
          </div>

          <div className="field">
            <label>Biography</label>
            <textarea
              rows="4"
              value={biography}
              onChange={(e) => setBiography(e.target.value)}
            />
          </div>

          <div className="field">
            <label>Language</label>
            <input
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
            />
          </div>

          {/* ================= DIGITAL SIGNATURE ================= */}
          {userData.role === "teacher" && (
            <div className="field">
              <label>Digital Signature</label>

              {signature && (
                <img
                  src={signature}
                  alt="Saved Signature"
                  width="200"
                  style={{ marginBottom: "8px" }}
                />
              )}

              <div className="signature-pad">
                <SignatureCanvas
                  ref={sigCanvasRef}
                  penColor="black"
                  canvasProps={{
                    width: 320,
                    height: 120,
                    className: "signature-canvas"
                  }}
                />
              </div>

              <div className="signature-actions">
                <button
                  type="button"
                  onClick={() => sigCanvasRef.current.clear()}
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={handleSaveSignature}
                  disabled={loading}
                >
                  Save Signature
                </button>
              </div>
            </div>
          )}


        <h3>Education</h3>
          <div className="field">
            <label>Occupation</label>
            <select value={occupation} onChange={(e) => setOccupation(e.target.value)}>
              <option value="">Select</option>
              {occupationOptions.map((o) => (
                <option key={o}>{o}</option>
              ))}
            </select>
          </div>

          <div className="field">
            <label>Education</label>
            <select value={education} onChange={(e) => setEducation(e.target.value)}>
              <option value="">Select</option>
              {educationOptions.map((e) => (
                <option key={e}>{e}</option>
              ))}
            </select>
          </div>

          <div className="field">
            <label>College</label>
            <input value={college} onChange={(e) => setCollege(e.target.value)} />
          </div>

          <h3>Location</h3>

          <div className="field">
            <label>Country</label>
            <select value={country} onChange={(e) => setCountry(e.target.value)}>
              <option value="">Select Country</option>
              {countries.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </div>

          <div className="field">
            <label>State</label>
            <select
              value={stateName}
              onChange={(e) => setStateName(e.target.value)}
              disabled={!country}
            >
              <option value="">Select State</option>
              {states.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </div>

          <div className="field">
            <label>City</label>
            <select
              value={city}
              onChange={(e) => setCity(e.target.value)}
              disabled={!stateName}
            >
              <option value="">Select City</option>
              {cities.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </div>

          <h3>Links</h3>

          <div className="field">
            <label>Website</label>
            <input value={website} onChange={(e) => setWebsite(e.target.value)} />
          </div>

          <div className="field">
            <label>Facebook</label>
            <input value={facebook} onChange={(e) => setFacebook(e.target.value)} />
          </div>

          <div className="field">
            <label>Instagram</label>
            <input value={instagram} onChange={(e) => setInstagram(e.target.value)} />
          </div>

          <div className="field">
            <label>LinkedIn</label>
            <input value={linkedin} onChange={(e) => setLinkedin(e.target.value)} />
          </div>

          <div className="field">
            <label>X</label>
            <input value={x} onChange={(e) => setX(e.target.value)} />
          </div>

          <div className="field">
            <label>YouTube</label>
            <input value={youtube} onChange={(e) => setYoutube(e.target.value)} />
          </div>

          <button className="save-btn" onClick={handleSave} disabled={loading}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default Profile;
